const formatterFor = (zone) => new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});
const numericParts = (instant, zone) => {
    const values = new Map(formatterFor(zone)
        .formatToParts(instant)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)]));
    const part = (name) => {
        const value = values.get(name);
        if (value === undefined || !Number.isInteger(value)) {
            throw new RangeError(`Unable to resolve ${name} in time zone ${zone}`);
        }
        return value;
    };
    return {
        year: part('year'),
        month: part('month'),
        day: part('day'),
        hour: part('hour'),
        minute: part('minute'),
    };
};
const wallEpoch = ({ year, month, day, hour, minute }) => Date.UTC(year, month - 1, day, hour, minute);
const twoDigits = (value) => String(value).padStart(2, '0');
const parseWallTime = (date, time) => {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
    if (dateMatch === null || timeMatch === null) {
        throw new RangeError('Date and time must use YYYY-MM-DD and HH:mm');
    }
    const parts = {
        year: Number(dateMatch[1]),
        month: Number(dateMatch[2]),
        day: Number(dateMatch[3]),
        hour: Number(timeMatch[1]),
        minute: Number(timeMatch[2]),
    };
    const normalized = new Date(wallEpoch(parts));
    if (normalized.getUTCFullYear() !== parts.year ||
        normalized.getUTCMonth() + 1 !== parts.month ||
        normalized.getUTCDate() !== parts.day ||
        normalized.getUTCHours() !== parts.hour ||
        normalized.getUTCMinutes() !== parts.minute) {
        throw new RangeError('Date and time do not form a valid wall-clock value');
    }
    return parts;
};
/** Convert a wall-clock entry in an explicit IANA zone to an ISO UTC instant. */
export const zonedWallTimeToUtc = (date, time, zone) => {
    const desired = parseWallTime(date, time);
    const desiredEpoch = wallEpoch(desired);
    let candidate = desiredEpoch;
    // An IANA offset is discovered from Intl rather than from the host time zone. Iteration handles
    // offsets that differ at the candidate and target instants (for example around DST transitions).
    for (let attempt = 0; attempt < 4; attempt += 1) {
        const represented = numericParts(new Date(candidate), zone);
        const correction = desiredEpoch - wallEpoch(represented);
        candidate += correction;
        if (correction === 0)
            break;
    }
    const resolved = numericParts(new Date(candidate), zone);
    if (wallEpoch(resolved) !== desiredEpoch) {
        throw new RangeError(`The wall-clock value does not exist in time zone ${zone}`);
    }
    return new Date(candidate).toISOString();
};
/** Convert an ISO instant to date/time fields in an explicit IANA zone. */
export const utcToZonedWallTime = (value, zone) => {
    const instant = new Date(value);
    if (!Number.isFinite(instant.getTime())) {
        throw new RangeError('Value must be a valid ISO instant');
    }
    const parts = numericParts(instant, zone);
    return {
        date: `${parts.year}-${twoDigits(parts.month)}-${twoDigits(parts.day)}`,
        time: `${twoDigits(parts.hour)}:${twoDigits(parts.minute)}`,
    };
};
//# sourceMappingURL=date-time.js.map